import { useEffect, useRef, useState } from "react";
import { api, apiError } from "../lib/api";
import { cachedGet, cacheSet, enqueue } from "../lib/offline";
import { useRealtime } from "../lib/realtime";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Camera, Thermometer, CheckCircle, Clock, WarningCircle, X, Plus, Eye, CalendarBlank, PencilSimple, Trash } from "@phosphor-icons/react";
import { DeptChips, useDepartments } from "../components/DepartmentPicker";

const STATUS = {
  pending: { chip: "bg-amber-50 text-amber-700 border-amber-200", accent: "border-l-amber-400", label: "Due" },
  overdue: { chip: "bg-red-50 text-red-700 border-red-200", accent: "border-l-red-500", label: "Overdue" },
  completed: { chip: "bg-emerald-50 text-emerald-700 border-emerald-200", accent: "border-l-emerald-500", label: "Done" },
};

export default function TasksPage() {
  const [tasks, setTasks] = useState([]);
  const [active, setActive] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [temp, setTemp] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const { user } = useAuth();
  const canManage = ["admin", "manager", "supervisor"].includes(user.role);
  const canEdit = ["admin", "manager"].includes(user.role);
  const [showAdhoc, setShowAdhoc] = useState(false);
  const [adhoc, setAdhoc] = useState({ title: "", due_time: "18:00", requires_photo: false, requires_temp: false, department_ids: [], assigned_user_ids: [] });
  const departments = useDepartments();
  const [team, setTeam] = useState([]);
  const [deptFilter, setDeptFilter] = useState("");
  const myDepts = user.department_ids || [];
  const isTopAdmin = ["admin", "gm"].includes(user.role) || user.is_master_operator;
  const scoped = myDepts.length > 0 && !isTopAdmin;
  const crossSelected = scoped && adhoc.department_ids.some((d) => !myDepts.includes(d));
  const activeDepts = departments.filter((d) => d.active !== false);
  useEffect(() => {
    if (["admin", "manager", "supervisor"].includes(user.role)) api.get("/team").then((r) => setTeam(r.data)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [adhocBusy, setAdhocBusy] = useState(false);
  const [proof, setProof] = useState(null);
  const [note, setNote] = useState("");
  const [editTask, setEditTask] = useState(null);
  const [viewDate, setViewDate] = useState("");
  const isHistory = !!viewDate;
  const todayStr = new Date().toISOString().slice(0, 10);

  const load = () => (deptFilter
    ? api.get("/tasks/today", { params: { department_id: deptFilter } }).then((r) => setTasks(r.data)).catch((e) => toast.error(apiError(e)))
    : cachedGet("/tasks/today", "tasks").then((r) => setTasks(r.data)).catch(() => {}));
  const loadHistory = (d) => api.get("/tasks/history", { params: { date: d } })
    .then((r) => setTasks(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => {
    if (viewDate) loadHistory(viewDate); else load();
    const onSync = () => !viewDate && load();
    window.addEventListener("sync-queue-changed", onSync);
    return () => window.removeEventListener("sync-queue-changed", onSync);
  }, [viewDate, deptFilter]); // eslint-disable-line react-hooks/exhaustive-deps
  useRealtime((msg) => { if (msg.type === "task.changed" && !viewDate) load(); });

  const openTask = (t) => { setActive(t); setPhoto(null); setTemp(""); setNote(""); };

  const removeTask = async (t) => {
    if (!window.confirm(`Delete task "${t.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/tasks/${t.id}`);
      toast.success("Task deleted");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveEdit = async () => {
    try {
      await api.put(`/tasks/${editTask.id}`, {
        title: editTask.title, description: editTask.description || "",
        due_time: editTask.due_time, role: editTask.role || "all", requires_photo: !!editTask.requires_photo,
      });
      toast.success("Task updated");
      setEditTask(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setPhoto(reader.result);
    reader.readAsDataURL(f);
  };

  const complete = async () => {
    setBusy(true);
    const body = { photo_base64: photo, temperature: temp === "" ? null : parseFloat(temp), note: note.trim() || null };
    try {
      await api.post(`/tasks/${active.id}/complete`, body);
      toast.success("Task verified & logged");
      setActive(null);
      load();
    } catch (e) {
      if (!e.response) {
        if (!enqueue({ path: `/tasks/${active.id}/complete`, body })) {
          toast.error("Photo is too large to queue offline — please retry when back online");
          return;
        }
        const updated = tasks.map((t) => t.id === active.id
          ? { ...t, status: "completed", completed_by: "You (pending sync)", on_time: true } : t);
        setTasks(updated);
        cacheSet("tasks", updated);
        toast.warning("You're offline — proof queued, it will sync when connection returns");
        setActive(null);
      } else {
        toast.error(apiError(e));
      }
    } finally {
      setBusy(false);
    }
  };

  const viewProof = async (t) => {
    try {
      const { data } = await api.get(`/tasks/${t.id}/proof`);
      setProof({ task: t, ...data });
    } catch (e) { toast.error(apiError(e)); }
  };

  const submitAdhoc = async () => {
    if (!adhoc.title.trim()) return toast.error("Task title is required");
    setAdhocBusy(true);
    try {
      await api.post("/tasks/adhoc", adhoc);
      toast.success("Shift task added & dispatched");
      setShowAdhoc(false);
      setAdhoc({ title: "", due_time: "18:00", requires_photo: false, requires_temp: false, department_ids: [], assigned_user_ids: [] });
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setAdhocBusy(false); }
  };

  const isOtherDept = (t) => {
    if (!scoped || user.role !== "manager") return false;
    const tgt = t.department_ids || [], org = t.origin_department_ids || [];
    const tgtMine = !tgt.length || tgt.some((d) => myDepts.includes(d));
    const orgMine = !org.length || org.some((d) => myDepts.includes(d));
    return (tgtMine && org.length > 0 && !orgMine) || (orgMine && tgt.length > 0 && !tgt.some((d) => myDepts.includes(d)));
  };
  const deptNames = (ids) => (ids || []).map((id) => departments.find((d) => d.id === id)?.name).filter(Boolean).join(", ");
  const crossLabel = (t) => {
    const org = t.origin_department_ids || [];
    const orgMine = !org.length || org.some((d) => myDepts.includes(d));
    return orgMine ? `Dispatched to: ${deptNames(t.department_ids) || "—"}` : `From: ${deptNames(org) || "—"}`;
  };
  const ownTasks = tasks.filter((t) => !isOtherDept(t));
  const otherTasks = tasks.filter(isOtherDept);

  const canSubmit = (!active?.requires_photo || photo) && (!active?.requires_temp || temp !== "");

  return (
    <div className="space-y-8 rise-in max-w-4xl mx-auto" data-testid="tasks-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-sm uppercase tracking-[0.2em] font-bold text-slate-400">Floor Task & Audit Engine</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mt-2">
            {isHistory ? `Tasks · ${viewDate}` : "Today's Tasks"}
          </h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 h-12">
            <CalendarBlank size={18} className="text-slate-400" />
            <input data-testid="tasks-date-picker" type="date" value={viewDate} max={todayStr}
              onChange={(e) => setViewDate(e.target.value === todayStr ? "" : e.target.value)}
              className="bg-transparent text-sm font-semibold outline-none" />
          </div>
          {isHistory && (
            <button data-testid="tasks-back-today" onClick={() => setViewDate("")}
              className="h-12 px-5 rounded-full border-2 border-slate-200 text-sm font-bold tap-press hover:border-slate-900">
              Back to today
            </button>
          )}
          {canManage && !isHistory && (
            <button data-testid="add-adhoc-task-button" onClick={() => setShowAdhoc(true)}
              className="h-12 px-6 rounded-full bg-slate-900 text-white text-sm font-bold flex items-center gap-2 tap-press hover:bg-slate-800">
              <Plus size={18} /> Add shift task
            </button>
          )}
        </div>
      </div>

      {isHistory && (
        <p data-testid="tasks-history-note" className="text-sm font-semibold text-slate-500 bg-slate-100 border border-slate-200 rounded-xl px-4 py-3 -mt-4">
          Historic view — read-only record of {viewDate}. Completion actions are disabled.
        </p>
      )}

      {isTopAdmin && !isHistory && activeDepts.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="dept-switcher">
          <button data-testid="dept-switch-all" onClick={() => setDeptFilter("")}
            className={`h-10 px-4 rounded-full text-xs font-bold tap-press border ${!deptFilter ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-500 border-slate-200 hover:border-slate-900"}`}>
            All departments
          </button>
          {activeDepts.map((d) => (
            <button key={d.id} data-testid={`dept-switch-${d.id}`} onClick={() => setDeptFilter(deptFilter === d.id ? "" : d.id)}
              className={`h-10 px-4 rounded-full text-xs font-bold tap-press border ${deptFilter === d.id ? "bg-sky-600 text-white border-sky-600" : "bg-white text-slate-500 border-slate-200 hover:border-slate-900"}`}>
              {d.name}
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-col gap-4" data-testid="task-list">
        {ownTasks.map((t) => {
          const st = STATUS[t.status];
          return (
            <div key={t.id} data-testid={`task-card-${t.id}`}
              className={`bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 border-l-4 ${st.accent} flex flex-col sm:flex-row sm:items-center gap-4`}>
              <div className="flex-1">
                <div className="flex items-center gap-3 flex-wrap">
                  <h3 className="font-heading font-bold text-lg">{t.title}</h3>
                  <span className={`text-xs font-bold px-3 py-1 rounded-full border ${st.chip}`}>{st.label}</span>
                </div>
                <p className="text-sm text-slate-500 mt-1">{t.description}</p>
                <div className="flex items-center gap-4 mt-3 text-xs font-semibold text-slate-400 flex-wrap">
                  <span className="flex items-center gap-1"><Clock size={16} /> Due {t.due_time}</span>
                  {t.template_id === "handover" && (
                    <span data-testid={`task-handover-chip-${t.id}`} className="text-amber-600 font-bold">From handover</span>
                  )}
                  <DeptChips ids={t.department_ids} departments={departments} testId={`task-dept-chips-${t.id}`} />
                  {(t.assigned_user_ids || []).includes(user.id) && (
                    <span data-testid={`task-assigned-you-${t.id}`} className="text-[10px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full">Assigned to you</span>
                  )}
                  {t.requires_photo && <span className="flex items-center gap-1"><Camera size={16} /> Photo proof</span>}
                  {t.requires_temp && <span className="flex items-center gap-1"><Thermometer size={16} /> Temp log</span>}
                  {t.status === "completed" && <span className="text-emerald-600">by {t.completed_by} {t.on_time ? "(on time)" : "(late)"}</span>}
                </div>
                {t.status === "completed" && (t.note || "").trim() && (
                  <p data-testid={`task-note-${t.id}`} className="mt-2 text-sm font-semibold text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                    Staff note: {t.note}
                  </p>
                )}
              </div>
              {t.status !== "completed" ? (
                isHistory ? (
                  <span className="text-xs font-bold text-slate-400 px-4">Not completed</span>
                ) : (
                  <div className="flex items-center gap-2 shrink-0">
                    {canEdit && (
                      <>
                        <button data-testid={`task-edit-button-${t.id}`} onClick={() => setEditTask({ ...t })}
                          className="h-11 w-11 rounded-full border border-slate-200 flex items-center justify-center text-slate-500 tap-press hover:border-slate-900" title="Edit task">
                          <PencilSimple size={18} />
                        </button>
                        <button data-testid={`task-delete-button-${t.id}`} onClick={() => removeTask(t)}
                          className="h-11 w-11 rounded-full border border-red-200 flex items-center justify-center text-red-500 tap-press hover:bg-red-50" title="Delete task">
                          <Trash size={18} />
                        </button>
                      </>
                    )}
                    <button data-testid={`task-complete-button-${t.id}`} onClick={() => openTask(t)}
                      className={`h-16 px-8 rounded-full font-heading font-bold text-white tap-press shrink-0 ${t.status === "overdue" ? "bg-red-500 hover:bg-red-600" : "bg-slate-900 hover:bg-slate-800"}`}>
                      {t.status === "overdue" ? "Resolve now" : "Complete"}
                    </button>
                  </div>
                )
              ) : (
                <div className="flex items-center gap-3 shrink-0">
                  {["admin", "manager"].includes(user.role) && (
                    <button data-testid={`task-view-proof-${t.id}`} onClick={() => viewProof(t)}
                      className="h-11 px-4 rounded-full border border-slate-200 text-xs font-bold text-slate-600 flex items-center gap-1.5 tap-press hover:border-slate-900">
                      <Eye size={16} /> Proof
                    </button>
                  )}
                  <CheckCircle size={40} weight="fill" className="text-emerald-500" />
                </div>
              )}
            </div>
          );
        })}
        {tasks.length === 0 && <p className="text-slate-400">{isHistory ? `No tasks were recorded on ${viewDate}.` : "No tasks scheduled for today."}</p>}
      </div>

      {otherTasks.length > 0 && (
        <section data-testid="other-dept-tasks">
          <h2 className="font-heading text-xl font-bold text-slate-700 mb-4">Other Department Tasks</h2>
          <div className="flex flex-col gap-3">
            {otherTasks.map((t) => {
              const st = STATUS[t.status];
              return (
                <div key={t.id} data-testid={`other-task-${t.id}`}
                  className={`bg-slate-50/70 border border-slate-200 rounded-2xl p-5 border-l-4 ${st.accent} flex flex-col sm:flex-row sm:items-center gap-3`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-heading font-bold">{t.title}</h3>
                      <span className={`text-xs font-bold px-3 py-1 rounded-full border ${st.chip}`}>{st.label}</span>
                      <span data-testid={`other-task-label-${t.id}`}
                        className="text-[10px] font-bold uppercase tracking-wider bg-indigo-50 text-indigo-700 border border-indigo-200 px-2.5 py-1 rounded-full">
                        {crossLabel(t)}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-xs font-semibold text-slate-400 flex-wrap">
                      <span className="flex items-center gap-1"><Clock size={16} /> Due {t.due_time}</span>
                      {t.created_by && <span>By {t.created_by}</span>}
                      {t.status === "completed" && <span className="text-emerald-600">by {t.completed_by} {t.on_time ? "(on time)" : "(late)"}</span>}
                    </div>
                  </div>
                  {t.status !== "completed" && !isHistory && (
                    <button data-testid={`other-task-complete-${t.id}`} onClick={() => openTask(t)}
                      className="h-12 px-6 rounded-full font-heading font-bold text-white bg-slate-900 hover:bg-slate-800 tap-press shrink-0">
                      Complete
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {active && (
        <div className="fixed inset-0 z-[1000] bg-slate-950/50 flex items-center justify-center p-4 sm:p-6" onClick={() => setActive(null)}>
          <div data-testid="task-proof-modal" onClick={(e) => e.stopPropagation()}
            className="bg-white w-full sm:max-w-lg max-h-[90vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl p-6 sm:p-8 rise-in">
            <div className="flex items-start justify-between">
              <div>
                {active.status === "overdue" && (
                  <p className="text-xs font-bold text-red-600 flex items-center gap-1 mb-1"><WarningCircle size={16} weight="fill" /> OVERDUE — escalated to manager</p>
                )}
                <h2 className="font-heading text-2xl font-bold">{active.title}</h2>
              </div>
              <button onClick={() => setActive(null)} className="p-2 rounded-full hover:bg-slate-100 tap-press" data-testid="task-modal-close"><X size={24} /></button>
            </div>

            {active.requires_photo && (
              <div className="mt-6">
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 mb-3">Photo proof (required)</p>
                {photo ? (
                  <div className="relative">
                    <img src={photo} alt="proof" className="w-full h-48 object-cover rounded-xl border border-slate-200" data-testid="task-photo-preview" />
                    <button onClick={() => setPhoto(null)} className="absolute top-2 right-2 bg-white rounded-full p-2 shadow tap-press"><X size={16} /></button>
                  </div>
                ) : (
                  <button data-testid="task-photo-button" onClick={() => fileRef.current?.click()}
                    className="w-full h-32 rounded-xl border-2 border-dashed border-slate-300 flex flex-col items-center justify-center gap-2 text-slate-500 font-bold tap-press hover:border-slate-900">
                    <Camera size={32} weight="duotone" /> Tap to capture / upload photo
                  </button>
                )}
                <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPhoto} data-testid="task-photo-input" />
              </div>
            )}

            {active.requires_temp && (
              <div className="mt-6">
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 mb-3">Temperature reading °C (required)</p>
                <input data-testid="task-temp-input" type="number" step="0.1" value={temp} onChange={(e) => setTemp(e.target.value)}
                  placeholder="e.g. 3.2"
                  className="w-full h-16 px-5 rounded-xl border border-slate-200 text-2xl font-heading font-bold focus:outline-none focus:ring-2 focus:ring-slate-900" />
                {temp !== "" && parseFloat(temp) > 4 && (
                  <p className="text-sm text-red-600 font-bold mt-2" data-testid="task-temp-warning">Above 4°C — corrective action required per SOP</p>
                )}
              </div>
            )}

            <div className="mt-6">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 mb-3">Staff notes (optional)</p>
              <textarea data-testid="task-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                placeholder='e.g. "Half completed due to stock delay", "Requires manager check"'
                className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" />
            </div>

            <button data-testid="task-submit-proof-button" onClick={complete} disabled={!canSubmit || busy}
              className="mt-8 w-full h-16 rounded-full bg-emerald-600 text-white font-heading font-bold text-lg tap-press hover:bg-emerald-700 disabled:opacity-40">
              {busy ? "Verifying…" : "Verify & complete task"}
            </button>
          </div>
        </div>
      )}

      {editTask && (
        <div className="fixed inset-0 z-[1000] bg-slate-950/50 flex items-center justify-center p-4 sm:p-6" onClick={() => setEditTask(null)}>
          <div data-testid="task-edit-modal" onClick={(e) => e.stopPropagation()} className="bg-white w-full sm:max-w-lg max-h-[90vh] overflow-y-auto rounded-3xl p-6 sm:p-8 rise-in">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl font-bold">Edit task</h2>
              <button onClick={() => setEditTask(null)} className="p-2 rounded-full hover:bg-slate-100 tap-press"><X size={24} /></button>
            </div>
            <div className="space-y-4">
              <input data-testid="task-edit-title" value={editTask.title} onChange={(e) => setEditTask({ ...editTask, title: e.target.value })}
                placeholder="Task title" className="w-full h-12 px-4 rounded-xl border border-slate-200" />
              <textarea data-testid="task-edit-description" value={editTask.description || ""} onChange={(e) => setEditTask({ ...editTask, description: e.target.value })}
                rows={2} placeholder="Description" className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm" />
              <input data-testid="task-edit-due" type="time" value={editTask.due_time} onChange={(e) => setEditTask({ ...editTask, due_time: e.target.value })}
                className="w-full h-12 px-4 rounded-xl border border-slate-200" />
              <label className="flex items-center gap-3 text-sm font-bold">
                <input type="checkbox" data-testid="task-edit-photo" checked={!!editTask.requires_photo}
                  onChange={(e) => setEditTask({ ...editTask, requires_photo: e.target.checked })} className="h-5 w-5" />
                Requires photo proof
              </label>
              <button data-testid="task-edit-save" onClick={saveEdit}
                className="w-full h-14 rounded-full bg-slate-900 text-white font-heading font-bold tap-press hover:bg-slate-800">Save changes</button>
            </div>
          </div>
        </div>
      )}

      {showAdhoc && (
        <div className="fixed inset-0 z-[1000] bg-slate-950/50 flex items-center justify-center p-4 sm:p-6" onClick={() => setShowAdhoc(false)}>
          <div data-testid="adhoc-task-modal" onClick={(e) => e.stopPropagation()} className="bg-white w-full sm:max-w-md max-h-[90vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl p-6 sm:p-8 rise-in">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl font-bold">Add shift task</h2>
              <button onClick={() => setShowAdhoc(false)} className="p-2 rounded-full hover:bg-slate-100 tap-press"><X size={24} /></button>
            </div>
            <div className="space-y-4">
              <input data-testid="adhoc-title-input" value={adhoc.title} onChange={(e) => setAdhoc({ ...adhoc, title: e.target.value })}
                placeholder="Task title (e.g. Deep clean fryer station)" className="w-full h-12 px-4 rounded-xl border border-slate-200" />
              <input data-testid="adhoc-due-input" type="time" value={adhoc.due_time} onChange={(e) => setAdhoc({ ...adhoc, due_time: e.target.value })}
                className="w-full h-12 px-4 rounded-xl border border-slate-200 font-semibold" />
              <div className="flex gap-6">
                <label className="flex items-center gap-2 text-sm font-semibold">
                  <input data-testid="adhoc-photo-checkbox" type="checkbox" checked={adhoc.requires_photo}
                    onChange={(e) => setAdhoc({ ...adhoc, requires_photo: e.target.checked })} className="h-5 w-5" /> Photo proof
                </label>
                <label className="flex items-center gap-2 text-sm font-semibold">
                  <input data-testid="adhoc-temp-checkbox" type="checkbox" checked={adhoc.requires_temp}
                    onChange={(e) => setAdhoc({ ...adhoc, requires_temp: e.target.checked })} className="h-5 w-5" /> Temp log
                </label>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.15em] font-bold text-slate-400 mb-2">Target department(s) — blank = whole property</p>
                <div className="flex flex-wrap gap-2" data-testid="adhoc-dept-picker">
                  {activeDepts.map((d) => {
                    const on = adhoc.department_ids.includes(d.id);
                    const foreign = scoped && !myDepts.includes(d.id);
                    return (
                      <button key={d.id} type="button" data-testid={`adhoc-dept-${d.id}`}
                        onClick={() => {
                          const next = on ? adhoc.department_ids.filter((x) => x !== d.id) : [...adhoc.department_ids, d.id];
                          const nextCross = scoped && next.some((x) => !myDepts.includes(x));
                          setAdhoc({ ...adhoc, department_ids: next, assigned_user_ids: nextCross ? [] : adhoc.assigned_user_ids });
                        }}
                        className={`h-9 px-4 rounded-full text-xs font-bold tap-press border ${on
                          ? (foreign ? "bg-indigo-600 text-white border-indigo-600" : "bg-sky-600 text-white border-sky-600")
                          : "bg-white text-slate-500 border-slate-200 hover:border-slate-900"}`}>
                        {d.name}{foreign ? " ⇢" : ""}
                      </button>
                    );
                  })}
                </div>
                {crossSelected && (
                  <p data-testid="adhoc-cross-note" className="text-[11px] font-semibold text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2 mt-2">
                    Cross-department dispatch — the task goes to the whole target department. Individual assignment is limited to your own department.
                  </p>
                )}
              </div>
              {!crossSelected && (
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] font-bold text-slate-400 mb-2">Assign to specific people (blank = whole department)</p>
                  <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto" data-testid="adhoc-assignee-picker">
                    {team.filter((m) => ["staff", "supervisor"].includes(m.role) &&
                      (scoped
                        ? (m.department_ids || []).some((d) => myDepts.includes(d))
                        : (adhoc.department_ids.length === 0 || !(m.department_ids || []).length || (m.department_ids || []).some((d) => adhoc.department_ids.includes(d))))).map((m) => (
                      <button key={m.id} type="button" data-testid={`adhoc-assignee-${m.id}`}
                        onClick={() => setAdhoc({ ...adhoc, assigned_user_ids: adhoc.assigned_user_ids.includes(m.id) ? adhoc.assigned_user_ids.filter((a) => a !== m.id) : [...adhoc.assigned_user_ids, m.id] })}
                        className={`h-9 px-4 rounded-full text-xs font-bold tap-press border ${adhoc.assigned_user_ids.includes(m.id)
                          ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-500 border-slate-200 hover:border-slate-900"}`}>
                        {m.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <button data-testid="adhoc-submit-button" onClick={submitAdhoc} disabled={adhocBusy}
                className="w-full h-14 rounded-full bg-slate-900 text-white font-heading font-bold tap-press hover:bg-slate-800 disabled:opacity-50">
                {adhocBusy ? "Adding…" : "Dispatch task"}
              </button>
            </div>
          </div>
        </div>
      )}

      {proof && (
        <div className="fixed inset-0 z-[1000] bg-slate-950/50 flex items-center justify-center p-4 sm:p-6" onClick={() => setProof(null)}>
          <div data-testid="proof-review-modal" onClick={(e) => e.stopPropagation()} className="bg-white w-full sm:max-w-lg max-h-[90vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl p-6 sm:p-8 rise-in">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-2xl font-bold">Audit proof</h2>
              <button data-testid="proof-review-close" onClick={() => setProof(null)} className="p-2 rounded-full hover:bg-slate-100 tap-press"><X size={24} /></button>
            </div>
            <p className="font-bold">{proof.task.title}</p>
            <p className="text-sm text-slate-500 mt-1">Completed by {proof.task.completed_by} {proof.task.on_time ? "(on time)" : "(late)"}</p>
            {proof.photo_base64 ? (
              <img src={proof.photo_base64} alt="proof" className="mt-4 w-full rounded-xl border border-slate-200" data-testid="proof-review-photo" />
            ) : (
              <p className="mt-4 text-sm text-slate-400">No photo attached.</p>
            )}
            {proof.temperature != null && (
              <p data-testid="proof-review-temp" className={`mt-4 font-heading font-bold text-2xl ${proof.temperature > 4 ? "text-red-600" : "text-emerald-600"}`}>{proof.temperature}°C</p>
            )}
            {proof.note && <p className="mt-2 text-sm text-slate-600">{proof.note}</p>}
          </div>
        </div>
      )}
    </div>
  );
}
